import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bot, ChevronDown, Ban, Undo2, Globe, Clock, RefreshCw } from "lucide-react";
import { fmtDuration } from "@/lib/fmtDuration";

const fmt = (n) => (n || 0).toLocaleString();
const cr = (n) => (n || 0).toFixed((n || 0) < 1 ? 4 : 2);

function Kpi({ label, value, sub, testid }) {
  return (
    <div className="min-w-0" data-testid={testid}>
      <p className="font-mono-brand text-[9px] tracking-[0.12em] uppercase text-propertic-muted">{label}</p>
      <p className="font-editorial text-xl text-propertic-navy tabular-nums leading-tight">{value}</p>
      {sub && <p className="text-[10px] text-propertic-muted">{sub}</p>}
    </div>
  );
}

function UsageRow({ r, i, onToggle, busy }) {
  return (
    <tr className="hover:bg-propertic-cream/30" data-testid={`roni-usage-row-${i}`}>
      <td className="px-3 py-2 font-mono-brand text-[10px] text-propertic-muted whitespace-nowrap">{new Date(r.first_at).toLocaleString()}</td>
      <td className="px-3 py-2 text-[11px]" title={r.referer || ""}>{r.source}<span className="block text-[9px] text-propertic-muted">{r.landing_path || "—"}</span></td>
      <td className="px-3 py-2 font-mono-brand text-[10px] whitespace-nowrap"><Globe className="w-3 h-3 inline-block mr-1 align-[-2px]" />{r.country || "—"}</td>
      <td className="px-3 py-2 text-[10px] text-propertic-muted max-w-[220px] truncate" title={r.user_agent}>{r.user_agent || "—"}</td>
      <td className="px-3 py-2 text-end tabular-nums" data-testid={`roni-usage-qa-${i}`}>{r.questions} / {r.answers}{r.refused ? <span className="block text-[9px] text-amber-700">{r.refused} refused</span> : null}</td>
      <td className="px-3 py-2 text-end font-mono-brand text-[10px] tabular-nums whitespace-nowrap"><Clock className="w-3 h-3 inline-block mr-1 align-[-2px] text-propertic-muted" />{fmtDuration(r.roni_seconds)}</td>
      <td className="px-3 py-2 text-end font-mono-brand text-[10px] tabular-nums" data-testid={`roni-usage-credits-${i}`}>{cr(r.est_credits)}</td>
      <td className="px-3 py-2 text-end">
        <button
          type="button"
          disabled={busy}
          onClick={() => onToggle(r)}
          data-testid={`roni-usage-block-btn-${i}`}
          className={`h-7 px-2.5 whitespace-nowrap rounded-full text-[10px] font-mono-brand uppercase inline-flex items-center gap-1 disabled:opacity-50 ${r.block_id ? "bg-propertic-cream text-propertic-navy" : "bg-red-600 text-white hover:bg-red-700"}`}
        >
          {r.block_id ? <><Undo2 className="w-3 h-3" /> Unblock</> : <><Ban className="w-3 h-3" /> Block Roni</>}
        </button>
      </td>
    </tr>
  );
}

export default function RoniUsagePanel({ range = "last_week" }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const reqId = React.useRef(0);
  const load = useCallback(() => {
    const id = ++reqId.current;
    api.get("/stats/roni-usage", { params: { range } })
      .then((r) => { if (id === reqId.current) setData(r.data); })
      .catch(() => { if (id === reqId.current) setData(null); });
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (r) => {
    if (!r.block_id && !window.confirm("Block this device and its network from using Roni? The rest of the website stays available.")) return;
    setBusy(true);
    try {
      if (r.block_id) await api.delete(`/admin/roni/block/${r.block_id}`);
      else await api.post(`/admin/roni/block/${encodeURIComponent(r.session_id)}`);
      toast.success(r.block_id ? "Roni unblocked for this visitor." : "This visitor can no longer use Roni.");
      load();
    } catch {
      toast.error("Could not update the block. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const s = data?.summary;
  const items = data?.items || [];
  return (
    <section className="mb-8" data-testid="roni-usage-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="roni-usage-toggle"
        className="w-full text-start rounded-2xl border hairline bg-white p-4 sm:p-5 transition-colors hover:bg-propertic-cream/30"
      >
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-xl bg-propertic-navy text-white flex items-center justify-center"><Bot className="w-4 h-4" /></span>
            <h2 className="font-editorial text-lg text-propertic-navy">Roni usage</h2>
            <span className="text-[10px] font-mono-brand uppercase tracking-[0.12em] text-propertic-muted">{data?.range_label || ""}</span>
          </div>
          <ChevronDown className={`w-4 h-4 text-propertic-muted transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
        {!data ? (
          <p className="text-[11px] text-propertic-muted"><RefreshCw className="w-3 h-3 animate-spin inline-block mr-1" /> Loading…</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <Kpi label="Questions asked" value={fmt(s.questions)} sub={`${fmt(s.chats)} chats`} testid="roni-kpi-questions" />
            <Kpi label="Answered" value={fmt(s.answers)} sub={`${fmt(s.failed)} failed`} testid="roni-kpi-answers" />
            <Kpi label="Time using Roni" value={fmtDuration(s.roni_seconds)} testid="roni-kpi-time" />
            <Kpi label="Est. credits" value={cr(s.est_credits)} sub="Gemini 2.5 Pro · estimate" testid="roni-kpi-credits" />
            <Kpi label="Refused" value={fmt(s.refused)} sub={`${data.blocks_active} blocked · cap ${data.rate_cap_per_hour}/h`} testid="roni-kpi-refused" />
          </div>
        )}
      </button>

      {open && data && (
        <div className="mt-3 bg-white rounded-2xl border hairline overflow-hidden" data-testid="roni-usage-details">
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="text-propertic-muted bg-propertic-cream/40">
                <tr>
                  {["First question", "Source", "Country", "Device", "Asked / answered", "Time on Roni", "Est. credits", ""].map((h, k) => (
                    <th key={h || k} className={`px-3 py-2 font-mono-brand text-[9px] uppercase tracking-wider whitespace-nowrap ${k >= 4 ? "text-end" : "text-start"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y hairline">
                {items.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-6 text-center text-[11px] text-propertic-muted">No Roni questions in this window yet.</td></tr>
                )}
                {items.map((r, i) => <UsageRow key={r.session_id} r={r} i={i} onToggle={toggle} busy={busy} />)}
              </tbody>
            </table>
          </div>
          <p className="px-4 py-2 text-[10px] text-propertic-muted border-t hairline">
            Credits are estimated from the text sent and received (about {data.rates.chars_per_token} characters per token at {data.rates.input_per_m} / {data.rates.output_per_m} credits per million input / output tokens). Your Emergent balance is the exact figure. Block stops that device and network from using Roni only.
          </p>
        </div>
      )}
    </section>
  );
}
